import Pills from '@rio-cloud/rio-uikit/Pills';
import { useCallback, useId, useState } from 'react';

import { handleEnterKeyInteraction } from '@/utils/accessibilityHandlers/handleEnterKeyInteraction';

import { TTab, TTabsProps } from './types';
import { createTabsFromConfig, validateUniqueTabTitles } from './utils';

const UiKitTabs = <T extends string>({
  tabConfig,
  defaultTab,
  onTabChange,
  className = '',
  contentClassName = '',
}: TTabsProps<T>) => {
  const tabs = createTabsFromConfig<T>(tabConfig);
  validateUniqueTabTitles(tabs);

  const [activeTab, setActiveTab] = useState<T>(defaultTab);
  const baseId = useId();

  const handleTabClick = useCallback(
    (tab: TTab<T>) => () => {
      if (tab.disabled || tab.title === activeTab) return;
      setActiveTab(tab.title);
      onTabChange?.(tab.title);
    },
    [onTabChange, activeTab],
  );

  const handleKeyDown = useCallback(
    (title: T, disabled: boolean) => (e: React.KeyboardEvent) => {
      const handler = handleEnterKeyInteraction(
        () => {
          setActiveTab(title);
          onTabChange?.(title);
        },
        { disabled, preventDefault: true },
      );

      handler(e);
    },
    [onTabChange],
  );

  const getTriggerId = (name: T) => `${baseId}-trigger-${name}`;
  const getPanelId = (name: T) => `${baseId}-panel-${name}`;
  const getTestId = (name: T) => `tab-${name}`;

  return (
    <>
      <Pills variant="filled" justified wordWrap className={className} role="tablist">
        {tabs.map((tab) => {
          const { title, disabled, label } = tab;
          const isActive = activeTab === title;
          const isDisabled = disabled ?? false;

          return (
            <Pills.Item
              key={title}
              id={getTriggerId(title)}
              role="tab"
              active={isActive}
              disabled={isDisabled}
              aria-controls={getPanelId(title)}
              aria-selected={isActive}
              tabIndex={isDisabled ? -1 : 0}
              onClick={handleTabClick(tab)}
              onKeyDown={handleKeyDown(title, isDisabled)}
              data-testid={getTestId(title)}
            >
              {label}
            </Pills.Item>
          );
        })}
      </Pills>

      <div className={`tab-content ${contentClassName}`.trim()}>
        {tabs.map((tab) => {
          const { title, content } = tab;
          const isActive = activeTab === title;

          return (
            <div
              key={title}
              id={getPanelId(title)}
              role="tabpanel"
              aria-labelledby={getTriggerId(title)}
              aria-hidden={!isActive}
              className={`tab-panel fade ${isActive ? 'active in' : ''}`.trim()}
            >
              {isActive && content}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default UiKitTabs;
